import { noop } from "lodash-es";

import type { AlterCard } from "@/backend/write/BackendWriter";
import { writer } from "@/backend/write/BackendWriter";
import type { Sticker } from "@/model/StickerType";
import type { AlmSourceId, BoardType, RiskType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { BoardCard, Card, Reaction } from "@/model/card";
import type { LinkingCard } from "@/model/link";
import type { Team } from "@/model/session";
import type { TargetStatus } from "@/model/status";
import type { Functionality, StickyType } from "@/model/stickyType";
import { isArtLevelWorkitem } from "@/model/stickyType";
import type { AuthUser } from "@/model/user";
import { fullUrl } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useDraggingStore } from "@/store/dragging";
import type { LinkedToItem } from "@/store/issueSelector";
import { boardCardToLinkedToItem, useIssueSelectorStore } from "@/store/issueSelector";
import { useLinkStore } from "@/store/link";
import { useServerSettingsStore } from "@/store/serverSettings";
import { useSessionStore } from "@/store/session";
import { NO_TEAM, useTeamStore } from "@/store/team";
import { useToastStore } from "@/store/toast";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import type { CardCreateProps } from "./addCard";
import { addCard, cardCreateProps, startAddCard } from "./addCard";
import { boardActions } from "./boardActions";
import { internalActions } from "./internalActions";
import { navigationActions } from "./navigationActions";
import { selectionActions } from "./selectionActions";
import type { ActionContext } from "./types";

export const cardActions = defineActions("card", {
  add: ctxAction(startAddCard, { name: /*$t*/ "contextMenu.createNew" }),
  addAtPointer: ctxAction((ctx) => startAddCard(ctx, { pos: "pointer" }), {
    name: /*$t*/ "contextMenu.createNew",
  }),
  togglePalette: action(noop, { icon: "general/add-new", name: /*$t*/ "action.addSticky" }),
  copy: action(noop),
  pasteMultiple: action(addMultipleCards, { name: /*$t*/ "action.paste" }),
  pasteText: ctxAction(startAddCard),
  duplicate: ctxAction(duplicate, {
    name: /*$t*/ "action.duplicate",
    label: /*$t*/ "label.actionMenu.duplicate",
    icon: "general/duplicate",
  }),
  delete: action(deleteCard, {
    name: /*$t*/ "cardAction.delete",
    label: /*$t*/ "label.actionMenu.delete",
    icon: "general/delete",
  }),
  remove: action(deleteCard, {
    name: /*$t*/ "cardAction.remove",
    label: /*$t*/ "label.actionMenu.remove",
    icon: "general/remove",
  }),
  unplan: ctxAction(unplan, {
    name: /*$t*/ "cardAction.unplan",
    label: /*$t*/ "label.actionMenu.unplan",
    icon: "general/return",
  }),
  mirror: ctxAction(
    (ctx, id: string, teamId: string | null, board: Board) => {
      ctx.track({
        functionality: useCardStore().cards[id].type.functionality,
        fromBoard: useBoardStore().currentBoard().type,
        toBoard: board.type,
      });
      if (!teamId && board.type === "team") {
        internalActions.setTeam(id, board.team.id);
      }
      return writer.mirror(id, board.id);
    },
    {
      name: /*$t*/ "cardAction.mirror",
      label: /*$t*/ "label.actionMenu.mirror",
      icon: "general/mirror",
    },
  ),
  /**
   * Adapted mirror action that can be used while dragging from the issue selector
   * (caller can specify tracking data, and should provide the position where the
   * new card should be rendered)
   */
  mirrorWhileDragging: ctxAction(
    (
      ctx,
      id: string,
      toBoard: Board,
      trackingData?: { functionality: Functionality; fromBoard: BoardType },
      newCardPos?: Pick<AlterCard, "x" | "y">,
    ) => {
      if (trackingData) {
        ctx.track({
          functionality: trackingData.functionality,
          fromBoard: trackingData.fromBoard,
          toBoard: toBoard.type,
        });
      }

      return writer.mirror(id, toBoard.id, undefined, newCardPos);
    },
  ),

  move: action((id: string, teamId: string) => writer.move(id, teamId), {
    name: /*$t*/ "cardAction.move",
    label: /*$t*/ "label.actionMenu.move",
    icon: "arrow/right-right",
  }),

  toProgram: action(
    () =>
      writer.mirror(
        useBoardStore().selectedStickyIds[0],
        useBoardsStore().boardByType("program").id,
      ),
    {
      name: /*$t*/ "cardAction.program",
      label: /*$t*/ "cardAction.program",
      icon: "general/mirror-planning-board",
    },
  ),

  toRisk: action((id: string, teamId: string) => {
    const boardId = useBoardsStore().boardByType("risk", {
      artId: useTeamStore().current.artId,
    }).id;
    const card = useCardStore().cards[id];
    writer.toRisk(id, boardId, card.text, card.type.id, teamId);
  }),

  toggleReaction: action(async (cardId: string, reaction: Reaction) => {
    if (useCardStore().hasCurrentUserReaction(cardId, reaction)) {
      await writer.removeReaction(cardId, reaction);
    } else {
      await writer.addReaction(cardId, reaction);
    }
  }),

  startAlter: action((id: string) => writer.startAlterCard(useBoardStore().currentBoard().id, id)),

  setAlmSource: ctxAction(
    (ctx, card: Card, almSourceId: AlmSourceId | null) => {
      useCardStore().setAlmSource({ id: card.id, almSourceId });
      writer.alterCard(useBoardStore().currentBoard().id, card.id, { almSourceId });
      ctx.track({ functionality: card.type.functionality });
    },
    {
      name: /*$t*/ "cardAction.project",
      label: /*$t*/ "label.actionMenu.project",
      icon: "general/project",
    },
  ),

  setPriority: ctxAction(
    (ctx, card: Card, priority: number, priorities?: boolean) => {
      ctx.track({ functionality: card.type.functionality });
      const { use, priority: prio } = useCardStore().setPriority({
        id: card.id,
        priority,
        priorities,
      });
      internalActions.alterCard(
        useBoardStore().currentBoard().id,
        card.id,
        { id: card.id, priority: prio },
        use,
      );
    },
    { label: /*$t*/ "label.actionMenu.wsjf" },
  ),

  setPoints: ctxAction(
    (ctx, card: Card, points: number) => {
      useCardStore().setPoints({ id: card.id, points });
      internalActions.alterCard(useBoardStore().currentBoard().id, card.id, { points });
      ctx.track({ functionality: card.type.functionality });
    },
    { label: /*$t*/ "label.actionMenu.storyPoints" },
  ),

  setFlag: ctxAction((ctx, card: Card, flagType: Sticker | null) => {
    const previous = card.flagType;
    useCardStore().setFlag({ id: card.id, flagType });
    internalActions.alterCard(useBoardStore().currentBoard().id, card.id, { flagType });
    ctx.track({
      functionality: card.type.functionality,
      hadPreviousValue: previous !== null,
      hasNewValue: flagType !== null,
    });
  }),

  setRisk: action((id: string, risk: RiskType) => {
    useCardStore().setRisk({ id, risk });
    internalActions.alterCard(useBoardStore().currentBoard().id, id, { risk });
  }),

  setText: action(
    (id: string, text: string) => {
      useCardStore().setText({ id, text });
      writer.alterCard(useBoardStore().currentBoard().id, id, { text });
    },
    { history: { merge: true, saveState: (id) => ({ id }) } },
  ),

  setTeam: action(
    (id: string, teamId: string) => {
      if (teamId === NO_TEAM.id) {
        internalActions.setTeam(id, null);
        internalActions.setIteration(id, null);
      } else {
        internalActions.setTeam(id, teamId);
        if (
          useServerSettingsStore().mirrorAssignedBacklogStickiesToTeamBoard &&
          useCardStore().cards[id].iterationId === null
        ) {
          const iterations = useSessionStore().iterations;
          if (iterations.length > 0) {
            internalActions.setIteration(id, iterations[0].id);
          }
        }
      }
    },
    { name: /*$t*/ "cardAction.team", label: /*$t*/ "label.actionMenu.team", icon: "persona/team" },
  ),

  setDepend: ctxAction(
    (ctx, id: string, team: Team | null) => {
      const { board, card } = useCardStore().setDependAction({ id, dependTeam: team || NO_TEAM });
      // board can be empty when depend on an external team
      if (board) {
        void writer.mirror(id, board.id, {
          flagType: card.flagType,
          precondTeam: card.precondTeam,
          dependTeam: card.dependTeam,
        });
      }
      internalActions.alterCard(useBoardStore().currentBoard().id, id, {
        flagType: card.flagType,
        precondTeam: card.precondTeam,
        dependTeam: card.dependTeam,
      });

      ctx.track({ functionality: card.type.functionality, isSameArt: card.artId === team?.artId });
    },
    {
      name: /*$t*/ "cardAction.depend",
      label: /*$t*/ "label.actionMenu.depend",
      icon: "persona/team",
    },
  ),

  setArt: action(
    (id: string, artId: string | null) => {
      useCardStore().setArt({ id, artId });
      internalActions.alterCard(useBoardStore().currentBoard().id, id, { artId });
    },
    { name: /*$t*/ "cardAction.art", label: /*$t*/ "label.actionMenu.art", icon: "general/art" },
  ),

  setAssignee: action((id: string, assignee: AuthUser | null) => {
    useCardStore().setAssignee(id, assignee);
    internalActions.alterCard(useBoardStore().currentBoard().id, id, { assignee });
  }),

  setType: ctxAction(
    (ctx, card: Card, type: StickyType) => {
      ctx.track({
        fromFunctionality: card.type.functionality,
        toFunctionality: type.functionality,
      });

      const { board, props, priorities } = useCardStore().setType({ id: card.id, type: type.id });
      writer.alterCard(board.id, card.id, props, priorities);
    },
    { name: /*$t*/ "stickyNote.stickyType", label: /*$t*/ "label.actionMenu.stickyType" },
  ),

  setStatus: ctxAction(
    (ctx, board: Board, card: Card, target: TargetStatus) => {
      ctx.track({
        functionality: card.type.functionality,
        fromStatus: card.status?.statusClass || "undefined",
        toStatus: target.status.statusClass,
      });
      const status = useCardStore().setStatus(board, card.id, target.status.name, {
        transition: target.transition.id,
      });
      internalActions.alterCard(board.id, card.id, { status, transition: target.transition.id });
    },
    { label: /*$t*/ "label.actionMenu.status" },
  ),

  // the actual moving is done continuously by mouse dragging
  // we need this here to track the action
  setPosition: action(noop, {
    history: { merge: true, saveState: (ids) => ({ id: ids.join("-") }) },
  }),

  /**
   * When 1 card is selected, open the assignee dropdown
   * by clicking on the chip (if it exists)
   */
  openAssigneeDropdown: ctxAction(
    (ctx) => {
      const cardIds = useBoardStore().selectedStickyIds;
      if (cardIds.length === 1) {
        const chipEl = document.getElementById(assigneeChipId(cardIds[0]));
        chipEl?.click();
      } else {
        ctx.track(null);
      }
    },
    { name: /*$t*/ "action.assign" },
  ),
  shareLink: action(
    (card: Card) => {
      void navigator.clipboard.writeText(fullUrl({ stickyId: card.id }));
      useToastStore().show(/*$t*/ "message.stickyLinkCopied", { messageParams: { count: 1 } });
    },
    {
      name: /*$t*/ "cardAction.share",
      label: /*$t*/ "label.actionMenu.share",
      icon: "general/share",
    },
  ),
  shareAlmLink: action(
    (card: Card) => {
      void navigator.clipboard.writeText(card.almIssueUrl!);
      useToastStore().show(/*$t*/ "message.almLinkCopied");
    },
    { name: /*$t*/ "label.actionMenu.almLinkToClipboard" },
  ),
  shareAlmId: action(
    (card: Card) => {
      void navigator.clipboard.writeText(card.almId!);
      useToastStore().show(/*$t*/ "message.almIdCopied");
    },
    { name: /*$t*/ "label.actionMenu.almIdToClipboard" },
  ),
  openDevLinks: untrackedAction((ctx) => ctx.track(null), {
    name: /*$t*/ "label.actionMenu.gitlabMRs",
    label: /*$t*/ "label.actionMenu.gitlabMRs",
    icon: "alm/gitlab-logo",
  }),
  openLinkModal: ctxAction(openLinkModal, {
    name: /*$t*/ "action.linkMultiple",
    label: /*$t*/ "label.actionMenu.link",
    icon: "general/link",
  }),
  togglePin: untrackedAction(
    (ctx) => {
      const cardIds = useBoardStore().selectedStickyIds;
      if (cardIds.length !== 1) {
        return ctx.track(null);
      }
      if (useLinkStore().isPinned(cardIds[0])) {
        cardActions.unpin(ctx, cardIds[0]);
      } else {
        cardActions.pin(ctx, cardIds[0]);
      }
    },
    { label: /*$t*/ "label.actionMenu.pin", icon: "general/pin" },
  ),
  pin: ctxAction((ctx, id: string) => {
    if (useLinkStore().isPinned(id) || useDraggingStore().isLinkDragging) {
      return ctx.track(null);
    }
    const card = useBoardStore().currentBoard().cards[id];
    ctx.track({ functionality: card.data.type.functionality });
    internalActions.markCardLinkedCards([card]);
    syncIssueSelectorLinkedTo(card);
  }),
  unpin: ctxAction((ctx, id: string) => {
    if (!useLinkStore().isPinned(id) || useDraggingStore().isLinkDragging) {
      return ctx.track(null);
    }
    const card = useBoardStore().currentBoard().cards[id];
    ctx.track({ functionality: card.data.type.functionality });
    useLinkStore().removeAllMarks();
    syncIssueSelectorLinkedTo(card, []);
  }),
});

/** Update the WIS "linked to" filter to reflect pin/unpin of an ART-level card */
function syncIssueSelectorLinkedTo(card: BoardCard, items?: LinkedToItem[]) {
  const issueSelector = useIssueSelectorStore();
  if (!issueSelector.active || !isArtLevelWorkitem(card.data) || !card.data.almId) return;

  issueSelector.setSelectedLinkedToItems(items ?? [boardCardToLinkedToItem(card)]);
  void issueSelector.fetchIssues();
}

function deleteCard(id: string, boardId: string) {
  const card = useCardStore().cards[id];
  const almId = card?.almId;

  if (useCardStore().delete({ id, boardId })) {
    writer.deleteCard(boardId, id, true);

    // remove from issue selector if it's an ALM-synced card being deleted from Jira
    if (almId && useIssueSelectorStore().active) {
      useIssueSelectorStore().removeIssue(almId);
    }
  }
}

function unplan(ctx: ActionContext, id: string, boardId: string) {
  const card = useCardStore().cards[id];
  if (!card) return;
  ctx.track({ functionality: card.type.functionality });

  // Both flavors reduce to a local+server delete of the team-board card:
  //   - Backlog mirror: deleting the mirror clears iteration on the origin
  //     server-side (mirror presence on a team-board swimlane IS the iteration
  //     assignment — see board/location/TeamBoardLocation.ts).
  //   - Team-level ALM-synced origin: the Work Item Selector repopulates on
  //     next fetch because iteration is cleared server-side.
  if (useCardStore().delete({ id, boardId })) {
    writer.deleteCard(boardId, id, false);
  }
}

async function duplicate(ctx: ActionContext) {
  useLinkStore().removeAllMarks();
  const cards = useBoardStore().selectedOrActiveStickies;
  if (cards.length === 1) {
    ctx.track({ functionality: cards[0].data.type.functionality });
    void startAddCard(ctx, cardCreateProps(cards[0], { findFreePos: true }), true);
  } else {
    const ids = await addMultipleCards(cards.map((card) => cardCreateProps(card)));
    selectionActions.duplicate(ctx, ids);
  }
}

export const assigneeChipId = (cardId: string) => `${cardId}-assignee-chip`;

// Suffix kept as `-flag-chip` (rather than `-sticker-chip`) so existing DOM
// selectors, e2e tests and analytics breadcrumbs that target this id stay
// stable. Renaming the suffix is a coordinated change.
export const stickerChipId = (cardId: string) => `${cardId}-flag-chip`;

async function addMultipleCards(cards: CardCreateProps[]) {
  boardActions.clearCardSelection("internal");
  return await Promise.all(
    cards.map(async (card) => {
      const id = await addCard(card, false);
      useBoardStore().selectSticky(id);
      return id;
    }),
  );
}

function openLinkModal(ctx: ActionContext) {
  const cardIds = useBoardStore().selectedStickyIds;
  const cardTargets = cardIds.flatMap((id) => {
    const card = useCardStore().cards[id];
    return card ? [{ type: "sticky", id: card.id } as LinkingCard] : [];
  });
  if (cardIds.length) {
    navigationActions.openModal(ctx, "LinkModal", { attrs: { targets: cardTargets } });
  }
}
